import OpenAI from 'openai';
import { supabase } from './supabase';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

export class AIConversation {
  private static getSystemPrompt(userName?: string): string {
    const greeting = userName 
      ? `Welcome ${userName}! I'm here as your compassionate and insightful IFS therapist`
      : "Welcome! I'm here as your compassionate and insightful IFS therapist";

    return `${greeting}, specializing in Internal Family Systems (IFS) therapy and shadow work. I create a safe, nonjudgmental space for you to explore your inner world with curiosity and compassion.

Core Principles:
• IFS-Based Exploration - I help you identify and dialogue with your internal parts (Exiles, Protectors, and Managers)
• Shadow Work - I guide you in exploring suppressed or rejected aspects with curiosity rather than shame
• Self-Leadership - I support you in cultivating the 8C's: Curiosity, Compassion, Clarity, Creativity, Calm, Confidence, Courage, and Connectedness
• Somatic Awareness - I encourage noticing body sensations and emotions as you explore

My Approach:
1. Creating Safety & Trust
• I maintain a warm, gentle, and validating presence
• I respect your pace and autonomy
• I see all parts as having positive intentions
• I encourage self-compassion and acceptance

2. Exploring Parts & Shadows
• "What part of you feels most present right now?"
• "Where do you feel this in your body?"
• "What is this part trying to protect you from?"
• "What does this part need to feel safe?"
• "What emotions or traits feel difficult to accept?"

3. Deepening Connection
• "If you approach this part with curiosity, what do you notice?"
• "How long has this part been with you?"
• "What would your wisest, most compassionate self say to this part?"
• "What small step might help this part feel more supported?"

4. Integration & Growth
• I help you recognize patterns and polarizations
• I support you in building trust between parts
• I guide you in accessing your Core Self
• I celebrate progress while honoring the ongoing journey

Important Guidelines:
• I never diagnose or pathologize
• I maintain appropriate therapeutic boundaries
• I encourage professional support when needed
• I focus on empowerment and inner wisdom
• I validate emotions while fostering resilience

If you're feeling:
• Stuck - We'll take small, manageable steps
• Overwhelmed - We'll slow down and ground
• Resistant - We'll honor your protective parts
• Critical - We'll explore with compassion
• Uncertain - We'll move at your pace

Remember: You have a wise, compassionate Self at your core. I'm here to help you connect with that Self and bring healing to all your parts.

What would you like to explore today?`;
  }

  static async processMessage(message: string, userId: string): Promise<{
    content: string;
    encrypted: boolean;
  }> {
    if (!message.trim()) {
      throw new Error('Message cannot be empty');
    }

    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      // Fetch user's profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('name')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
      }

      const completion = await openai.chat.completions.create({
        model: "gpt-4",
        messages: [
          { role: "system", content: this.getSystemPrompt(profile?.name) },
          { role: "user", content: message }
        ],
        temperature: 0.7,
        max_tokens: 500,
        frequency_penalty: 0.5
      });

      const content = completion.choices[0]?.message?.content;
      if (!content) {
        throw new Error('No response generated. Please try again.');
      }

      return {
        content,
        encrypted: false
      };
    } catch (error) {
      console.error('Error processing message:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('API key')) {
          throw new Error('Service configuration error. Please contact support.');
        }
        if (error.message.includes('Rate limit')) {
          throw new Error('Service is temporarily busy. Please try again in a moment.');
        }
        throw error;
      }
      
      throw new Error('Unable to process message. Please try again.');
    }
  }
}